import { useState, useEffect } from "react";
import { Mail, Trash2, Check, Shield, User, Clock, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "../components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { getMessages, markMessageRead, deleteMessage, getTeams } from "../lib/api";
import { toast } from "sonner";

export default function Messages() {
  const [messages, setMessages] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [messagesRes, teamsRes] = await Promise.all([
        getMessages(),
        getTeams(),
      ]);
      setMessages(messagesRes.data);
      setTeams(teamsRes.data);
    } catch (error) {
      toast.error("Failed to fetch messages");
    } finally {
      setLoading(false);
    }
  };

  const getTeamName = (teamId) => {
    const team = teams.find((t) => t.id === teamId);
    return team?.name || "Unknown Team";
  };

  const handleMarkRead = async (id) => {
    try {
      await markMessageRead(id);
      setMessages((prev) =>
        prev.map((m) => (m.id === id ? { ...m, is_read: true } : m))
      );
    } catch (error) {
      toast.error("Failed to mark as read");
    }
  };

  const handleDelete = async () => {
    if (!selectedMessage) return;
    try {
      await deleteMessage(selectedMessage.id);
      setMessages((prev) => prev.filter((m) => m.id !== selectedMessage.id));
      toast.success("Message deleted");
      setDeleteDialogOpen(false);
      setSelectedMessage(null);
    } catch (error) {
      toast.error("Failed to delete message");
    }
  };

  const openDeleteDialog = (msg) => {
    setSelectedMessage(msg);
    setDeleteDialogOpen(true);
  };

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
    if (expandedId !== id) {
      const msg = messages.find((m) => m.id === id);
      if (msg && !msg.is_read) {
        handleMarkRead(id);
      }
    }
  };

  const unreadCount = messages.filter((m) => !m.is_read).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div data-testid="messages-page" className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-4xl md:text-5xl font-extrabold uppercase tracking-tight">
            Messages
          </h1>
          <p className="text-muted-foreground mt-2">
            {unreadCount > 0
              ? `${unreadCount} unread message${unreadCount > 1 ? "s" : ""}`
              : "All messages read"}
          </p>
        </div>
      </div>

      {/* Messages List */}
      {messages.length > 0 ? (
        <div className="space-y-4">
          {messages.map((msg, index) => (
            <div
              key={msg.id}
              className={`bg-card border rounded-xl overflow-hidden animate-fade-in opacity-0 ${
                msg.is_read ? "border-border" : "border-primary/50"
              }`}
              style={{ animationDelay: `${index * 30}ms` }}
              data-testid={`message-${msg.id}`}
            >
              {/* Header */}
              <div
                className="p-4 cursor-pointer hover:bg-secondary/30 transition-colors"
                onClick={() => toggleExpand(msg.id)}
              >
                <div className="flex items-start gap-4">
                  <div
                    className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      msg.is_read
                        ? "bg-secondary text-muted-foreground"
                        : "bg-primary/20 text-primary"
                    }`}
                  >
                    <Mail className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium">{msg.sender_name}</span>
                      {!msg.is_read && (
                        <span className="w-2 h-2 bg-primary rounded-full" />
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground truncate">
                      {msg.subject}
                    </p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {msg.recipient_type === "admin"
                          ? "To: League Admin"
                          : `To: ${getTeamName(msg.team_id)} Captain`}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(msg.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        openDeleteDialog(msg);
                      }}
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      data-testid={`delete-message-${msg.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                    {expandedId === msg.id ? (
                      <ChevronUp className="w-5 h-5 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-muted-foreground" />
                    )}
                  </div>
                </div>
              </div>

              {/* Expanded Content */}
              {expandedId === msg.id && (
                <div className="px-4 pb-4 border-t border-border">
                  <div className="pt-4 pl-14">
                    <p className="text-sm text-muted-foreground mb-2">
                      From: {msg.sender_email}
                    </p>
                    <div className="bg-background rounded-lg p-4">
                      <p className="whitespace-pre-wrap">{msg.message}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-card border border-border rounded-xl p-12 text-center">
          <Mail className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
          <h3 className="font-heading text-xl font-bold uppercase mb-2">
            No Messages Yet
          </h3>
          <p className="text-muted-foreground">
            Messages from the contact form will appear here
          </p>
        </div>
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-xl font-bold uppercase">
              Delete Message
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this message from{" "}
              {selectedMessage?.sender_name}?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-border">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-message-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
